import React from "react";
import { RDonutChart } from "../../ChartUtility/recharts";
import { useTrainingCoverage } from "./useTrainingCoverage";
import { colorAt, formatDate, PAGE_SIZE } from "./helpers";
import "./TrainingCoverage.css";

/**
 * Fires a row/card activation on Enter or Space so keyboard users get the same
 * drill-down as a click.
 * @param {React.KeyboardEvent} event
 * @param {() => void} action
 */
const onActivateKey = (event, action) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

/**
 * Training Coverage drill-down: Category → Training → Detail. A program run on several
 * dates is grouped into one entry; the detail shows the principles covered, the
 * combined attendee split across all its sessions, and a per-session (per date-range)
 * split — each slice a percentage of that scope's attendees. Data and navigation live
 * in {@link useTrainingCoverage}; this component only renders.
 *
 * @param {object} props
 * @param {number} props.financialYearId - selected financial year, supplied by the
 *   Training Dashboard's filter component.
 * @param {boolean} [props.embedded] - when true, hides the standalone page title so the
 *   component sits inside the Training Dashboard's tab instead of a full page.
 */
export default function TrainingCoverage({ financialYearId, embedded = false }) {
  const {
    view,
    loading,
    categories,
    categoriesError,
    activeCategory,
    trainings,
    trainingsError,
    page,
    search,
    detail,
    detailError,
    openCategory,
    onSearchChange,
    changePage,
    openTraining,
    goToCategories,
    retryCategories,
    retryTrainings,
    retryDetail,
  } = useTrainingCoverage(financialYearId);

  const totalPages = Math.max(1, Math.ceil((trainings.total || 0) / PAGE_SIZE));
  const splitTypes = detail?.combined?.split?.map((slice) => slice.label) || [];

  const skeletonRows = (count) => (
    <div className="tcov-skel-list">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="tcov-skel-row" style={{ "--i": i }} />
      ))}
    </div>
  );

  const errorState = (message, onRetry) => (
    <div className="tcov-error" role="alert">
      <span>{message}</span>
      <button type="button" onClick={onRetry}>
        Retry
      </button>
    </div>
  );

  return (
    <div className={`tcov${embedded ? " tcov--embedded" : ""}`}>
      <header className="tcov-head">
        {!embedded && <h2 className="tcov-title">Training Coverage</h2>}
        <nav className="tcov-crumbs">
          <button className="tcov-crumb" onClick={goToCategories}>
            Categories
          </button>
          {activeCategory && (view === "trainings" || view === "detail") && (
            <>
              <span className="tcov-sep">/</span>
              <button className="tcov-crumb" onClick={() => openCategory(activeCategory)}>
                {activeCategory.title}
              </button>
            </>
          )}
          {view === "detail" && detail && (
            <>
              <span className="tcov-sep">/</span>
              <span className="tcov-crumb tcov-crumb--active">{detail.trainingTitle}</span>
            </>
          )}
        </nav>
      </header>

      {!financialYearId && (
        <p className="tcov-empty">Select a financial year to view coverage.</p>
      )}

      {financialYearId && view === "categories" && (
        <div className="tcov-view">
          {loading ? (
            <div className="tcov-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="tcov-skel-card" style={{ "--i": i }} />
              ))}
            </div>
          ) : categoriesError ? (
            errorState("Couldn't load training categories.", retryCategories)
          ) : categories.length === 0 ? (
            <p className="tcov-empty">No training categories found.</p>
          ) : (
            <div className="tcov-grid">
              {categories.map((category, i) => (
                <button
                  key={category.id}
                  className="tcov-card tcov-rise"
                  style={{ "--i": i }}
                  onClick={() => openCategory(category)}
                >
                  <span className="tcov-card-title">{category.title}</span>
                  <span className="tcov-card-count">
                    {category.trainingCount} training
                    {category.trainingCount === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {financialYearId && view === "trainings" && (
        <div className="tcov-view">
          <div className="tcov-searchbar">
            <input
              className="tcov-search"
              type="search"
              aria-label="Search trainings by name"
              placeholder="Search trainings by name"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>

          {loading ? (
            skeletonRows(6)
          ) : trainingsError ? (
            errorState("Couldn't load trainings.", retryTrainings)
          ) : trainings.items.length === 0 ? (
            <p className="tcov-empty">
              {search.trim()
                ? `No trainings match "${search.trim()}".`
                : "No trainings in this category."}
            </p>
          ) : (
            <>
              <table className="tcov-table">
                <thead>
                  <tr>
                    <th>Training</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Sessions</th>
                    <th>Principles</th>
                    <th>Attendees</th>
                  </tr>
                </thead>
                <tbody>
                  {trainings.items.map((row, i) => (
                    <tr
                      key={row.trainingTitle}
                      className="tcov-row tcov-rise"
                      style={{ "--i": i }}
                      role="button"
                      tabIndex={0}
                      onClick={() => openTraining(row.trainingIds)}
                      onKeyDown={(e) => onActivateKey(e, () => openTraining(row.trainingIds))}
                    >
                      <td>{row.trainingTitle}</td>
                      <td>{formatDate(row.fromDate)}</td>
                      <td>{formatDate(row.toDate)}</td>
                      <td>{row.sessions}</td>
                      <td>{row.principleCount}</td>
                      <td>{row.attendeeCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tcov-pager">
                <button disabled={page <= 0} onClick={() => changePage(page - 1)}>
                  Previous
                </button>
                <span>
                  Page {page + 1} of {totalPages} · {trainings.total} total
                </span>
                <button
                  disabled={page + 1 >= totalPages}
                  onClick={() => changePage(page + 1)}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {financialYearId && view === "detail" && (
        <div className="tcov-view">
          {loading ? (
            skeletonRows(4)
          ) : detailError ? (
            errorState("Couldn't load this training.", retryDetail)
          ) : !detail ? (
            skeletonRows(4)
          ) : (
            <div className="tcov-detail">
              <section className="tcov-panel" style={{ "--i": 0 }}>
                <h3 className="tcov-panel-title">Principles covered</h3>
                {detail.principles.length === 0 ? (
                  <p className="tcov-empty">No principles recorded.</p>
                ) : (
                  <ul className="tcov-chips">
                    {detail.principles.map((principle, i) => (
                      <li key={principle} className="tcov-chip" style={{ "--i": i }}>
                        {principle}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="tcov-panel" style={{ "--i": 1 }}>
                <h3 className="tcov-panel-title">
                  Attendees by type ({detail.combined.totalAttendees} total)
                  {detail.sessions.length > 1 && (
                    <span className="tcov-panel-note">
                      {" "}· across {detail.sessions.length} sessions
                    </span>
                  )}
                </h3>
                {detail.combined.totalAttendees === 0 ? (
                  <p className="tcov-empty">No attendees recorded for this training.</p>
                ) : (
                  <div className="tcov-split">
                    <div className="tcov-chart">
                      <RDonutChart
                        height={240}
                        showLegend={false}
                        unit="attendees"
                        data={detail.combined.split.map((slice, i) => ({
                          name: slice.label,
                          value: slice.count,
                          color: colorAt(i),
                        }))}
                      />
                    </div>
                    <table className="tcov-table tcov-split-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Attendees</th>
                          <th>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.combined.split.map((slice, i) => (
                          <tr key={slice.label}>
                            <td>
                              <span
                                className="tcov-swatch"
                                style={{ background: colorAt(i) }}
                              />
                              {slice.label}
                            </td>
                            <td>{slice.count}</td>
                            <td>
                              <div className="tcov-share">
                                <span className="tcov-share-val">{slice.percent}%</span>
                                <span className="tcov-share-track">
                                  <span
                                    className="tcov-share-fill"
                                    style={{ "--pct": slice.percent, "--i": i, background: colorAt(i) }}
                                  />
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {detail.sessions.length > 1 && (
                <section className="tcov-panel" style={{ "--i": 2 }}>
                  <h3 className="tcov-panel-title">By session</h3>
                  <table className="tcov-table">
                    <thead>
                      <tr>
                        <th>From</th>
                        <th>To</th>
                        <th>Attendees</th>
                        {splitTypes.map((type) => (
                          <th key={type}>{type}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.sessions.map((session) => {
                        const bySession = new Map(
                          session.split.map((slice) => [slice.label, slice.count])
                        );
                        return (
                          <tr key={session.trainingId}>
                            <td>{formatDate(session.fromDate)}</td>
                            <td>{formatDate(session.toDate)}</td>
                            <td>{session.totalAttendees}</td>
                            {splitTypes.map((type) => (
                              <td key={type}>{bySession.get(type) || 0}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </section>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
